import { env } from '@/config/env.ts'

export interface TestResult {
  pass: boolean
  error?: string
  duration: number
}

export interface TestCase {
  id: string
  name: string
  category: string
  run: () => Promise<TestResult>
}

async function runTest(fn: () => Promise<void>): Promise<TestResult> {
  const start = performance.now()
  try {
    await fn()
    return { pass: true, duration: performance.now() - start }
  } catch (e) {
    return {
      pass: false,
      error: e instanceof Error ? e.message : String(e),
      duration: performance.now() - start,
    }
  }
}

function assert(condition: boolean, message: string) {
  if (!condition) throw new Error(message)
}

// ---------------------------------------------------------------------------
// Service Module Tests
// ---------------------------------------------------------------------------

const serviceModuleTests: TestCase[] = [
  {
    id: 'svc-encryption-load',
    name: 'Encryption service module loads',
    category: 'Service Modules',
    run: () =>
      runTest(async () => {
        const mod = await import('@/services/encryption/index.ts')
        assert(
          typeof mod.createLitEncryptionService === 'function',
          'createLitEncryptionService not exported',
        )
        assert(
          typeof mod.getLitNetwork === 'function',
          'getLitNetwork not exported',
        )
      }),
  },
  {
    id: 'svc-ipfs-load',
    name: 'IPFS service module loads',
    category: 'Service Modules',
    run: () =>
      runTest(async () => {
        const mod = await import('@/services/ipfs/index.ts')
        assert(
          typeof mod.createPinataService === 'function',
          'createPinataService not exported',
        )
      }),
  },
  {
    id: 'svc-contract-load',
    name: 'Contract service module loads',
    category: 'Service Modules',
    run: () =>
      runTest(async () => {
        const mod = await import('@/services/contract/index.ts')
        assert(
          typeof mod.createDeadmanSwitchService === 'function',
          'createDeadmanSwitchService not exported',
        )
      }),
  },
  {
    id: 'svc-notification-load',
    name: 'Notification service module loads',
    category: 'Service Modules',
    run: () =>
      runTest(async () => {
        const mod = await import('@/services/notification/index.ts')
        assert(
          typeof mod.createNotificationService === 'function',
          'createNotificationService not exported',
        )
      }),
  },
  {
    id: 'svc-database-load',
    name: 'Database service module loads',
    category: 'Service Modules',
    run: () =>
      runTest(async () => {
        const mod = await import('@/services/database/index.ts')
        assert(
          typeof mod.createSupabaseDatabase === 'function',
          'createSupabaseDatabase not exported',
        )
      }),
  },
  {
    id: 'svc-services-index',
    name: 'Services index instantiates all services',
    category: 'Service Modules',
    run: () =>
      runTest(async () => {
        const s = await import('@/services/index.ts')
        assert(!!s.encryption, 'encryption service not instantiated')
        assert(!!s.ipfs, 'ipfs service not instantiated')
        assert(!!s.deadmanSwitch, 'contract service not instantiated')
        assert(!!s.notifications, 'notification service not instantiated')
        assert(!!s.db, 'database service not instantiated')
      }),
  },
]

// ---------------------------------------------------------------------------
// Encryption Tests
// ---------------------------------------------------------------------------

const encryptionTests: TestCase[] = [
  {
    id: 'enc-instantiate',
    name: 'Encryption service instantiates without errors',
    category: 'Encryption',
    run: () =>
      runTest(async () => {
        const { createLitEncryptionService } =
          await import('@/services/encryption/lit.service.ts')
        const svc = createLitEncryptionService()
        assert(typeof svc.connect === 'function', 'connect() missing')
        assert(
          typeof svc.encryptString === 'function',
          'encryptString() missing',
        )
        assert(typeof svc.decrypt === 'function', 'decrypt() missing')
        assert(
          svc.isReady() === false,
          'isReady() should be false before connect',
        )
      }),
  },
  {
    id: 'enc-connect-disconnect',
    name: 'Encryption service connect/disconnect cycle',
    category: 'Encryption',
    run: () =>
      runTest(async () => {
        const { createLitEncryptionService } =
          await import('@/services/encryption/lit.service.ts')
        const svc = createLitEncryptionService()
        await svc.connect()
        assert(svc.isReady() === true, 'isReady() should be true after connect')
        await svc.disconnect()
        assert(
          svc.isReady() === false,
          'isReady() should be false after disconnect',
        )
      }),
  },
  {
    id: 'enc-config',
    name: 'Encryption config returns valid network',
    category: 'Encryption',
    run: () =>
      runTest(async () => {
        const { getLitNetwork } =
          await import('@/services/encryption/lit.config.ts')
        assert(getLitNetwork() === 'chipotle', 'Expected Chipotle backend')
      }),
  },
]

// ---------------------------------------------------------------------------
// API Connectivity Tests
// ---------------------------------------------------------------------------

const apiTests: TestCase[] = [
  {
    id: 'api-supabase',
    name: 'Supabase endpoint is reachable',
    category: 'API Connectivity',
    run: () =>
      runTest(async () => {
        assert(!!env.supabaseUrl, 'VITE_SUPABASE_URL not configured')
        const res = await fetch(`${env.supabaseUrl}/rest/v1/`, {
          method: 'HEAD',
          headers: { apikey: env.supabaseAnonKey },
        })
        assert(
          res.status < 500,
          `Supabase returned ${res.status}: ${res.statusText}`,
        )
      }),
  },
  {
    id: 'api-pinata',
    name: 'Pinata gateway is reachable',
    category: 'API Connectivity',
    run: () =>
      runTest(async () => {
        assert(!!env.pinataGateway, 'VITE_PINATA_GATEWAY not configured')
        const url = env.pinataGateway.startsWith('http')
          ? env.pinataGateway
          : `https://${env.pinataGateway}`
        const res = await fetch(url, { method: 'HEAD', mode: 'no-cors' })
        // no-cors returns opaque response (status 0), just checking it doesn't throw
        assert(
          res.status === 0 || res.status < 500,
          `Pinata gateway returned ${res.status}`,
        )
      }),
  },
]

// ---------------------------------------------------------------------------
// Routing Tests
// ---------------------------------------------------------------------------

const routingTests: TestCase[] = [
  {
    id: 'route-home',
    name: 'Homepage renders hero section',
    category: 'Routing & UI',
    run: () =>
      runTest(async () => {
        // We're on the test-runner page, so check the page itself loaded
        assert(!!document.querySelector('body'), 'Body element missing')
        // Navigate check: fetch homepage as text to verify it returns HTML
        const res = await fetch('/')
        assert(res.ok, `Homepage returned ${res.status}`)
        const html = await res.text()
        assert(html.includes('</html>'), 'Homepage did not return valid HTML')
      }),
  },
  {
    id: 'route-protected-redirect',
    name: 'Protected routes return valid HTML (redirect handled client-side)',
    category: 'Routing & UI',
    run: () =>
      runTest(async () => {
        const routes = ['/dashboard', '/profile', '/settings']
        for (const route of routes) {
          const res = await fetch(route)
          assert(
            res.ok,
            `Route ${route} returned ${res.status} (SPA should return 200 for all routes)`,
          )
        }
      }),
  },
]

// ---------------------------------------------------------------------------
// Config / Environment Tests
// ---------------------------------------------------------------------------

const configTests: TestCase[] = [
  {
    id: 'cfg-required-vars',
    name: 'Required environment variables are present',
    category: 'Configuration',
    run: () =>
      runTest(async () => {
        const required: [string, unknown][] = [
          ['VITE_SUPABASE_URL', env.supabaseUrl],
          ['VITE_SUPABASE_ANON_KEY', env.supabaseAnonKey],
          ['VITE_CONTRACT_ADDRESS', env.contractAddress],
          ['VITE_REOWN_PROJECT_ID', env.reownProjectId],
        ]
        const missing = required.filter(([, val]) => !val).map(([name]) => name)
        assert(missing.length === 0, `Missing env vars: ${missing.join(', ')}`)
      }),
  },
  {
    id: 'cfg-optional-vars',
    name: 'Optional environment variables check',
    category: 'Configuration',
    run: () =>
      runTest(async () => {
        const warnings: string[] = []
        if (!env.pinataGateway) warnings.push('VITE_PINATA_GATEWAY')
        if (!env.rpcUrl) warnings.push('VITE_RPC_URL')
        if (warnings.length > 0) {
          // Still pass, but note what's missing
          return
        }
      }),
  },
  {
    id: 'cfg-contract-address',
    name: 'Contract address is valid hex',
    category: 'Configuration',
    run: () =>
      runTest(async () => {
        assert(!!env.contractAddress, 'Contract address not set')
        assert(
          /^0x[0-9a-fA-F]{40}$/.test(env.contractAddress),
          `Invalid contract address format: ${env.contractAddress}`,
        )
      }),
  },
]

// ---------------------------------------------------------------------------
// Stress Tests (imported from separate module to keep this file focused)
// ---------------------------------------------------------------------------

import { stressTestCases } from './stressTestCases.ts'

// ---------------------------------------------------------------------------
// Export all tests
// ---------------------------------------------------------------------------

export const allTestCases: TestCase[] = [
  ...serviceModuleTests,
  ...encryptionTests,
  ...apiTests,
  ...routingTests,
  ...configTests,
  ...stressTestCases,
]

export const testCategories = [...new Set(allTestCases.map((t) => t.category))]
